import React, { useState, useMemo, useEffect, useCallback } from "react";
import { VIEW_TYPES } from "../../shared/Scheduler/constants";
import {
  useGetScheduleQuery,
  useGetUnassignedJobsQuery,
  useUpdateEventMutation,
  useAssignResourcesMutation,
} from "../../redux/services/schedule/scheduleApi";
import {
  startOfDay,
  endOfDay,
  addDays,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
} from "date-fns";
import { Event, TruckingEvent } from "../../shared/Scheduler/types/events";
import { Resource } from "../../shared/Scheduler/types/resources";
import { ViewType } from "../../shared/Scheduler/types/common";
import "./styles.css";
import { DispatchEventModal } from "@/modals";
import { toast } from "react-toastify";
import { Spinner } from "@/shared";
import { useFetchAll, useCrumbs, useResourceGeolocations } from "@/hooks";
import {
  MapIcon,
  CalendarIcon,
  Squares2X2Icon,
} from "@heroicons/react/24/outline";
import DispatchMapPanel from "./components/DispatchMapPanel";
import DispatchQueuePanel from "./components/DispatchQueuePanel";
import DispatchSchedulerPanel from "./components/DispatchSchedulerPanel";
import DispatchWorkspace from "./components/DispatchWorkspace";
import {
  applyOptimisticEventPatch,
  clearOptimisticEventPatch,
  mergeEventsWithOptimisticPatches,
} from "./dispatchOptimisticEvents";

const buildDateRangeForView = (
  view: ViewType,
  date: Date,
  currentRange?: { start: Date; end: Date },
) => {
  if (view === VIEW_TYPES.TIMELINE) {
    return (
      currentRange || {
        start: startOfDay(date),
        end: endOfDay(addDays(date, 4)),
      }
    );
  }

  if (view === VIEW_TYPES.DAY || view === VIEW_TYPES.LIST) {
    return {
      start: startOfDay(date),
      end: endOfDay(date),
    };
  }

  if (view === VIEW_TYPES.WEEK) {
    return {
      start: startOfWeek(date, { weekStartsOn: 1 }),
      end: endOfWeek(date, { weekStartsOn: 1 }),
    };
  }

  if (view === VIEW_TYPES.MONTH) {
    return {
      start: startOfMonth(date),
      end: endOfMonth(date),
    };
  }

  return {
    start: startOfDay(date),
    end: endOfDay(addDays(date, 4)),
  };
};

export const Dispatch = () => {
  const { setRightContent } = useCrumbs();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [schedulerView, setSchedulerView] = useState<ViewType>(
    VIEW_TYPES.TIMELINE as ViewType,
  );
  const [isMapVisible, setIsMapVisible] = useState(false);
  const [isSchedulerVisible, setIsSchedulerVisible] = useState(true);
  const [isDispatchLayoutVisible, setIsDispatchLayoutVisible] = useState(true);
  const [verticalSplitSizes, setVerticalSplitSizes] = useState([50, 50]);
  const [threeWaySplitSizes, setThreeWaySplitSizes] = useState([
    33.33, 33.33, 33.34,
  ]); // Map, Scheduler, DispatchLayout - equal thirds

  // Calculate initial date range using useMemo to ensure it's computed correctly
  const useDateRange = schedulerView === (VIEW_TYPES.TIMELINE as ViewType);

  const initialDateRange = useMemo(
    () => buildDateRangeForView(VIEW_TYPES.TIMELINE as ViewType, selectedDate),
    [selectedDate],
  );

  const [dateRange, setDateRange] = useState(initialDateRange);
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [selectedResource, setSelectedResource] = useState<Resource | null>(
    null,
  );
  const [selectedSlot, setSelectedSlot] = useState<{
    start: Date;
    end: Date;
    resourceId?: string;
  } | null>(null);
  const [optimisticEventPatches, setOptimisticEventPatches] = useState<
    Record<string, Partial<Event>>
  >({});

  // Fetch schedule via generic useFetchAll hook
  const {
    filters: { setFilters },
    queryResult: {
      data: scheduleData,
      isLoading: isLoadingSchedule,
      error: scheduleError,
      refetch: refetchSchedule,
    },
  } = useFetchAll<any>({
    queryHook: useGetScheduleQuery as any,
    defaultPageSize: 50,
    defaultFilters: [
      {
        id: "startDate",
        value: startOfDay(dateRange.start).toISOString(),
      },
      {
        id: "endDate",
        value: endOfDay(dateRange.end).toISOString(),
      },
    ],
  });

  // Keep filters in sync with selected date range
  useEffect(() => {
    setFilters([
      { id: "startDate", value: startOfDay(dateRange.start).toISOString() },
      { id: "endDate", value: endOfDay(dateRange.end).toISOString() },
    ]);
  }, [dateRange, setFilters]);

  // // Get drivers data for the DispatchTable
  // const { data: driversData } = useGetDriversQuery({
  //   page: 1,
  //   limit: 100,
  // });

  // Add the update event mutation
  const [updateEvent, { isLoading: isUpdatingEvent, error: updateEventError }] =
    useUpdateEventMutation();

  // Add the assign resources mutation
  const [, { isLoading: isAssigningResources }] = useAssignResourcesMutation();

  // Fetch unassigned jobs for distance calculation
  const { data: unassignedJobsData } = useGetUnassignedJobsQuery({
    page: 1,
    limit: 100,
  });

  const handleDateChange = (newDate: Date) => {
    setSelectedDate(newDate);
    if (!useDateRange) {
      setDateRange(buildDateRangeForView(schedulerView, newDate));
    }
  };

  const handleDateRangeChange = (newRange: { start: Date; end: Date }) => {
    if (newRange.start && newRange.end) {
      // Ensure we're using startOfDay and endOfDay for consistent behavior
      const normalizedRange = {
        start: startOfDay(newRange.start),
        end: endOfDay(newRange.end),
      };
      setDateRange(normalizedRange);
      setSelectedDate(normalizedRange.start);
    }
  };

  // Memoize map center to prevent re-renders
  const mapCenter = useMemo(() => ({ lat: 33.435315, lng: -112.159875 }), []);
  const resolvedScheduleData = scheduleData as any;

  // Combine resources from schedule data
  const resources: Resource[] = useMemo(() => {
    return [
      // ...sampleResources,
      ...(resolvedScheduleData?.resources?.map((resource: any) => ({
        ...resource,
        // Ensure consistent ID format - scheduler expects 'id' property
        id: resource.id || resource._id,
        // Ensure consistent name format - scheduler expects 'name' property
        name: resource.name || resource.fullName,
        // Preserve shift status and location
        hasActiveShift: !!(resource.hasActiveShift || resource.activeShift),
        shiftLocation: resource.shiftLocation,
        activeShift: resource.activeShift,
      })) || []),
    ].filter(
      (resource, index, self) =>
        // Remove duplicates based on ID
        index === self.findIndex((r) => r.id === resource.id),
    );
  }, [resolvedScheduleData?.resources]);

  // Use live geolocation hook to get resources with real-time location data
  const {
    resourcesWithLocations: resourcesWithLiveLocations,
    resourcesWithActiveLocations: resourcesWithLocation,
    isConnected: isGeoConnected,
    activeLocationCount,
  } = useResourceGeolocations(resources);

  const getResourceId = useCallback((resource: any) => {
    return (
      resource?.id ||
      resource?._id ||
      resource?.userId ||
      resource?.user?._id ||
      resource?.user?.id
    );
  }, []);

  const resourceById = useMemo(() => {
    const map = new globalThis.Map<string, any>();
    resourcesWithLiveLocations.forEach((resource: any) => {
      const resourceId = getResourceId(resource);
      if (resourceId) {
        map.set(String(resourceId), resource);
      }
    });
    return map;
  }, [resourcesWithLiveLocations, getResourceId]);

  // Helper function to calculate distance between two coordinates (Haversine formula)
  const calculateDistance = (
    lat1: number,
    lng1: number,
    lat2: number,
    lng2: number,
  ): number => {
    const R = 3959; // Earth's radius in miles
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLng = ((lng2 - lng1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // Get job location coordinates
  const getJobLocation = (job: any): { lat: number; lng: number } | null => {
    // Try to get location from various possible fields
    const location =
      job.location ||
      job.serviceLocation ||
      job.client?.address ||
      job.legs?.[0]?.route?.origin;

    if (!location) return null;

    // Check for coordinates
    if (location.coordinates?.latitude && location.coordinates?.longitude) {
      return {
        lat: location.coordinates.latitude,
        lng: location.coordinates.longitude,
      };
    }
    if (location.lat && location.lng) {
      return { lat: location.lat, lng: location.lng };
    }

    // If no coordinates, we can't calculate distance
    // In a real implementation, you might geocode the address
    return null;
  };

  // Calculate closest unassigned jobs for a given resource location
  const getClosestUnassignedJobs = useMemo(() => {
    return (resourceLocation: { lat: number; lng: number }) => {
      const jobs = unassignedJobsData?.jobs || [];

      const jobsWithDistance = jobs
        .map((job: any) => {
          const jobLocation = getJobLocation(job);
          if (!jobLocation) return null;

          const distance = calculateDistance(
            resourceLocation.lat,
            resourceLocation.lng,
            jobLocation.lat,
            jobLocation.lng,
          );

          return {
            ...job,
            distance,
            jobLocation,
          };
        })
        .filter(Boolean)
        .sort((a: any, b: any) => a.distance - b.distance)
        .slice(0, 3); // Get top 3 closest

      return jobsWithDistance;
    };
  }, [unassignedJobsData]);

  // Only show scheduled events in the scheduler (with date filtering)
  const scheduledEventsBase: Event[] = useMemo(() => {
    return [
      // ...generateSampleEvents(selectedDate),
      ...(resolvedScheduleData?.events?.map((event: Event) => {
        const normalizedEvent = {
          ...event,
          // Extract just the IDs from assignedResources objects
          assignedResources: Array.isArray(event.assignedResources)
            ? event.assignedResources.map((r: any) =>
                typeof r === "string" ? r : r._id || r.id,
              )
            : [],
          // Ensure timeRange dates are Date objects
          timeRange: {
            ...event.timeRange,
            start: new Date(event.timeRange.start),
            end: new Date(event.timeRange.end),
          },
        };

        return normalizedEvent;
      }) || []),
    ];
  }, [resolvedScheduleData?.events]);

  const scheduledEvents: Event[] = useMemo(
    () => mergeEventsWithOptimisticPatches(scheduledEventsBase, optimisticEventPatches),
    [optimisticEventPatches, scheduledEventsBase],
  );

  useEffect(() => {
    if (!selectedEvent) return;

    const refreshedEvent = scheduledEvents.find(
      (event) =>
        String(event.id || event._id) ===
        String(selectedEvent.id || selectedEvent._id),
    );

    if (!refreshedEvent) return;
    if (refreshedEvent === selectedEvent) return;

    setSelectedEvent(refreshedEvent);
  }, [scheduledEvents, selectedEvent]);

  const handleDispatchModalUpdate = useCallback(
    async (payload?: any) => {
      if (!payload?.type) {
        await refetchSchedule();
        return;
      }

      const eventId = payload?.eventId ? String(payload.eventId) : "";

      if (payload.type === "apply" && eventId && payload.patch) {
        setOptimisticEventPatches((currentPatches) =>
          applyOptimisticEventPatch(currentPatches, eventId, payload.patch),
        );
        return;
      }

      if (payload.type === "rollback" && eventId) {
        setOptimisticEventPatches((currentPatches) =>
          clearOptimisticEventPatch(currentPatches, eventId),
        );
        return;
      }

      if (payload.type === "commit") {
        await refetchSchedule();

        if (eventId) {
          setOptimisticEventPatches((currentPatches) =>
            clearOptimisticEventPatch(currentPatches, eventId),
          );
        }
      }
    },
    [refetchSchedule],
  );

  const handleQueueDataUpdate = useCallback(
    async (payload?: any) => {
      if (!payload?.type) {
        await refetchSchedule();
        return;
      }

      if (
        payload.type === "assignment" &&
        payload.eventId &&
        payload.patch
      ) {
        const eventId = String(payload.eventId);

        setOptimisticEventPatches((currentPatches) =>
          applyOptimisticEventPatch(currentPatches, eventId, payload.patch),
        );

        await refetchSchedule();

        setOptimisticEventPatches((currentPatches) =>
          clearOptimisticEventPatch(currentPatches, eventId),
        );
      }
    },
    [refetchSchedule],
  );

  // Custom function to get event resource IDs
  const getEventResourceIds = (event: Event) => {
    if (!event.assignedResources) return [];

    // Handle both string IDs and object with _id/_id properties
    return event.assignedResources.map((resource: any) => {
      if (typeof resource === "string") return resource;
      return resource._id || resource.id || resource;
    });
  };

  const handleEventChange = async (updatedEvent: Event) => {
    try {
      const result = await updateEvent({
        id: updatedEvent.id,
        assignedResources: updatedEvent.assignedResources,
        timeRange: {
          start: updatedEvent.timeRange?.start,
          end: updatedEvent.timeRange?.end,
        },
      }).unwrap();

      toast.success("Event updated successfully");

      // Refetch both schedule and unassigned jobs
      refetchSchedule();

      return result;
    } catch (error) {
      console.error("Failed to update event:", error);
      toast.error("Failed to update event");
      throw error;
    }
  };

  const handleEventDrop = useCallback(
    async (eventId: string, newStartTime: Date, newResource?: Resource) => {
      const currentEvent = scheduledEvents.find(
        (event) => event.id === eventId,
      );
      if (!currentEvent) {
        return;
      }

      const currentStart = new Date(currentEvent.timeRange.start);
      const currentEnd = new Date(currentEvent.timeRange.end);
      const duration = currentEnd.getTime() - currentStart.getTime();

      await handleEventChange({
        ...currentEvent,
        assignedResources: newResource
          ? [String(newResource.id)]
          : currentEvent.assignedResources,
        timeRange: {
          start: newStartTime,
          end: new Date(newStartTime.getTime() + duration),
        },
      });
    },
    [handleEventChange, scheduledEvents],
  );

  // Centralized function to handle resource assignment
  const handleResourceAssignment = async (
    eventId: string,
    resourceIds: string[],
  ) => {
    // console.log("handleResourceAssignment", eventId, resourceIds);
    try {
      const result = await updateEvent({
        id: eventId,
        assignedResources: resourceIds,
      }).unwrap();

      toast.success("Resource assignment updated successfully");

      // Refetch both queries since assigning a resource moves it from unassigned to scheduled
      refetchSchedule();

      return result;
    } catch (error) {
      console.error("Failed to update resource assignment:", error);
      toast.error("Failed to update resource assignment");
      throw error;
    }
  };

  // Handle dropping unassigned jobs onto the scheduler
  const handleUnassignedJobDrop = async (
    jobId: string,
    targetResourceId: string,
    targetTime?: Date,
  ) => {
    try {
      const updatePayload: any = {
        id: jobId,
        assignedResources: [targetResourceId],
      };

      // If a target time is provided, update the time range as well
      if (targetTime) {
        updatePayload.timeRange = {
          start: targetTime.toISOString(),
        };
      }

      const result = await updateEvent(updatePayload).unwrap();

      toast.success("Job assigned to resource successfully");

      // Refetch both queries to update the display
      refetchSchedule();

      return result;
    } catch (error) {
      console.error("Failed to assign job to resource:", error);
      toast.error("Failed to assign job to resource");
      throw error;
    }
  };

  const handleEventClick = useCallback((event: Event) => {
    setSelectedResource(null);
    setSelectedSlot(null);
    setClosestJobs([]);
    setSelectedEvent(event);
    setIsEventModalOpen(true);
  }, []);

  // State for closest jobs when clicking on a resource
  const [closestJobs, setClosestJobs] = useState<any[]>([]);

  // Handle clicking on a resource marker (driver/technician on shift but not on a route)
  const handleResourceClick = useCallback(
    (resource: any) => {
      const resourceId = getResourceId(resource);
      const resolvedResource =
        (resourceId && resourceById.get(String(resourceId))) || resource;

      // Calculate closest unassigned jobs if resource has location
      if (
        resolvedResource.currentLocation?.lat &&
        resolvedResource.currentLocation?.lng
      ) {
        const closest = getClosestUnassignedJobs(
          resolvedResource.currentLocation,
        );
        setClosestJobs(closest);
      } else {
        setClosestJobs([]);
      }

      // Set the resource and open the modal
      // We pass the resource info to the modal for display
      setSelectedResource(resolvedResource as Resource);
      setSelectedEvent(null); // Clear any selected event
      setIsEventModalOpen(true);
    },
    [getClosestUnassignedJobs, getResourceId, resourceById],
  );

  // Memoized callback for map event clicks (looks up event by ID)
  const handleMapEventClick = useCallback(
    (eventId: string) => {
      const event = scheduledEvents.find((e) => e.id === eventId);
      if (event) {
        handleEventClick(event);
      }
    },
    [scheduledEvents, handleEventClick],
  );

  const handleViewChange = (newView: ViewType) => {
    setSchedulerView(newView);
    setDateRange((currentRange) =>
      buildDateRangeForView(newView, selectedDate, currentRange),
    );
  };

  const handleSlotSelect = (slotInfo: {
    start: Date;
    end: Date;
    resourceId?: string;
  }) => {
    setSelectedSlot(slotInfo);
    if (slotInfo.resourceId) {
      setSelectedResource(
        resources.find((r) => r.id === slotInfo.resourceId) || null,
      );
    }
    setIsEventModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsEventModalOpen(false);
    setSelectedEvent(null);
    setSelectedSlot(null);
    setSelectedResource(null);
  };

  const handleMapToggle = () => {
    setIsMapVisible((prev) => {
      const next = !prev;

      if (next) {
        // Opening map - reset to equal thirds so the newly-visible pane
        // gets a predictable highlighted/layout state.
        setThreeWaySplitSizes([33.33, 33.33, 33.34]);
      }

      return next;
    });
  };

  const handleSchedulerToggle = () => {
    setIsSchedulerVisible((prev) => !prev);
  };

  const handleDispatchLayoutToggle = () => {
    setIsDispatchLayoutVisible((prev) => !prev);
  };

  // Set view controls in breadcrumbs area
  useEffect(() => {
    setRightContent(
      <div
        className="flex items-center gap-2 transition-all duration-500 ease-in-out"
        style={{
          marginRight:
            isEventModalOpen && (selectedEvent || selectedResource)
              ? "512px"
              : "0",
        }}
      >
        <span className="text-sm font-medium text-gray-500">View:</span>
        <button
          onClick={handleMapToggle}
          className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
            isMapVisible
              ? "bg-teal-100 text-teal-800 border-teal-400 shadow-sm"
              : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
          }`}
          title={isMapVisible ? "Hide Map" : "Show Map"}
        >
          <span className="flex items-center gap-1.5">
            <MapIcon className="w-4 h-4" />
            Map
          </span>
        </button>
        <button
          onClick={handleSchedulerToggle}
          className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
            isSchedulerVisible
              ? "bg-blue-50 text-blue-700 border-blue-300"
              : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
          }`}
          title={isSchedulerVisible ? "Hide Calendar" : "Show Calendar"}
        >
          <span className="flex items-center gap-1.5">
            <CalendarIcon className="w-4 h-4" />
            Calendar
          </span>
        </button>
        <button
          onClick={handleDispatchLayoutToggle}
          className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
            isDispatchLayoutVisible
              ? "bg-purple-50 text-purple-700 border-purple-300"
              : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
          }`}
          title={
            isDispatchLayoutVisible
              ? "Hide Unassigned Jobs"
              : "Show Unassigned Jobs"
          }
        >
          <span className="flex items-center gap-1.5">
            <Squares2X2Icon className="w-4 h-4" />
            Unassigned
          </span>
        </button>
      </div>,
    );

    // Cleanup on unmount
    return () => {
      setRightContent(null);
    };
  }, [
    isMapVisible,
    isSchedulerVisible,
    isDispatchLayoutVisible,
    isEventModalOpen,
    selectedEvent,
    selectedResource,
    setRightContent,
  ]);

  // Calculate visible sections and their sizes
  const visibleSections = useMemo(() => {
    const sections = [];
    if (isMapVisible) sections.push("map");
    if (isSchedulerVisible) sections.push("scheduler");
    if (isDispatchLayoutVisible) sections.push("dispatchLayout");
    let sizes = [];

    if (sections.length === 3) {
      sizes = threeWaySplitSizes;
    } else if (sections.length === 2) {
      sizes = verticalSplitSizes;
    } else if (sections.length === 1) {
      sizes = [100];
    }

    return { sections, sizes };
  }, [
    isMapVisible,
    isSchedulerVisible,
    isDispatchLayoutVisible,
    threeWaySplitSizes,
    verticalSplitSizes,
  ]);

  // Extract routes from scheduled events for map display
  const mapRoutes = useMemo(() => {
    return scheduledEvents
      .filter((event) => {
        // Only include trucking events with route data
        return (
          event.industryType === "trucking" &&
          (event as TruckingEvent).route?.pickupLocation &&
          (event as TruckingEvent).route?.dropoffLocation
        );
      })
      .map((event) => {
        const truckingEvent = event as TruckingEvent;
        const route = truckingEvent.route;

        // Get origin and destination (coordinates or address)
        const getLocationValue = (location: any) => {
          if (
            location?.coordinates?.latitude &&
            location?.coordinates?.longitude
          ) {
            return {
              lat: location.coordinates.latitude,
              lng: location.coordinates.longitude,
            };
          }
          if (location?.address?.fullAddress) {
            return location.address.fullAddress;
          }
          if (location?.address) {
            const addr = location.address;
            const parts = [
              addr.address,
              addr.city,
              addr.state,
              addr.zipcode,
            ].filter(Boolean);
            if (parts.length > 0) {
              return parts.join(", ");
            }
          }
          if (typeof location?.address === "string") {
            return location.address;
          }
          return null;
        };

        const origin = getLocationValue(route.pickupLocation);
        const destination = getLocationValue(route.dropoffLocation);

        if (!origin || !destination) return null;

        // Get driver name from assigned resources
        const driverIds = event.assignedResources || [];
        const driver = resources.find((r) => driverIds.includes(r.id));
        const driverName = driver?.name || "Unassigned";
        const driverAvatar =
          (driver as any)?.avatar ||
          (driver as any)?.profilePicture ||
          (driver as any)?.image ||
          null;

        return {
          origin,
          destination,
          driverName,
          driverAvatar,
          eventId: event.id,
          eventTitle: event.title,
          estimatedDuration: route.estimatedDuration || null,
        };
      })
      .filter(Boolean);
  }, [scheduledEvents, resources]);

  const isLoading = isLoadingSchedule;
  const hasError = scheduleError;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <Spinner size="lg" />
          <p className="mt-4 text-gray-600">Loading dispatch data...</p>
        </div>
      </div>
    );
  }

  if (hasError) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">⚠️</div>
          <p className="text-red-600">Error loading dispatch data</p>
          <button
            onClick={() => {
              refetchSchedule();
              // refetchUnassigned();
            }}
            className="mt-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ height: "100vh", overflow: "hidden" }} className="relative">
      <div
        className="transition-all duration-500 ease-in-out h-full"
        style={{
          marginRight:
            isEventModalOpen && (selectedEvent || selectedResource)
              ? "512px"
              : "0",
        }}
      >
        <DispatchWorkspace
          sections={visibleSections.sections}
          sizes={visibleSections.sizes}
          onSplitResize={(sizes) => {
            if (!sizes || sizes.length !== visibleSections.sections.length) {
              return;
            }

            if (visibleSections.sections.length === 3) {
              setThreeWaySplitSizes(sizes);
              return;
            }

            if (visibleSections.sections.length === 2) {
              setVerticalSplitSizes(sizes);
            }
          }}
          mapPanel={
            <DispatchMapPanel
              routes={mapRoutes}
              resources={resourcesWithLocation}
              center={mapCenter}
              onEventClick={handleMapEventClick}
              onResourceClick={handleResourceClick}
            />
          }
          schedulerPanel={
            <DispatchSchedulerPanel
              scheduledEvents={scheduledEvents}
              resources={resourcesWithLiveLocations}
              getEventResourceIds={getEventResourceIds}
              selectedDate={selectedDate}
              setSelectedDate={handleDateChange}
              onEventChange={handleEventChange}
              onEventDrop={handleEventDrop}
              onEventClick={handleEventClick}
              onResourceClick={handleResourceClick}
              onTimeSlotClick={handleSlotSelect}
              onViewChange={handleViewChange}
              activeView={schedulerView}
              selectedDateRange={dateRange}
              onDateRangeChange={handleDateRangeChange}
              useDateRange={useDateRange}
              showMapButton={true}
              onMapToggle={handleMapToggle}
              isMapVisible={isMapVisible}
              showSchedulerToggle={true}
              onSchedulerToggle={handleSchedulerToggle}
              isSchedulerVisible={isSchedulerVisible}
              isDraggable={true}
            />
          }
          queuePanel={
            <DispatchQueuePanel
              resources={resources}
              selectedDate={selectedDate}
              dateRange={dateRange}
              onDataUpdate={handleQueueDataUpdate}
              onResourceAssignment={handleResourceAssignment}
              onUnassignedJobDrop={handleUnassignedJobDrop}
              isUpdating={isUpdatingEvent || isAssigningResources}
              onModalOpen={() => {
                if (isEventModalOpen) {
                  handleCloseModal();
                }
              }}
              onDispatchLayoutToggle={handleDispatchLayoutToggle}
              isDispatchLayoutVisible={isDispatchLayoutVisible}
            />
          }
        />
      </div>
      <DispatchEventModal
        key={
          selectedEvent
            ? `event-${selectedEvent.id || selectedEvent._id}`
            : selectedResource
              ? `resource-${selectedResource.id || (selectedResource as any)?._id || "unknown"}`
              : selectedSlot
                ? `slot-${
                    selectedSlot.resourceId || "no-resource"
                  }-${selectedSlot.start?.toISOString?.() || "no-start"}`
                : "dispatch-modal"
        }
        event={selectedEvent}
        visible={
          isEventModalOpen &&
          (!!selectedEvent || !!selectedResource || !!selectedSlot)
        }
        setVisible={setIsEventModalOpen}
        onClose={handleCloseModal}
        slotInfo={selectedSlot}
        resource={selectedResource}
        closestJobs={closestJobs}
        onUpdate={handleDispatchModalUpdate}
      />
    </div>
  );
};

export default Dispatch;
